import { connect, useSelector } from 'react-redux';
import styled from 'styled-components';
import { SectionContainer, SectionHeaderCont, SectionSubHeader } from '../styles';
import { SectionCont, HeaderSubCont, PolicyTitle, PolicyPrice } from '../Policies/styles';
import Checkbox from '../../../elements/Checkbox';
import ShieldIcon from '../../../icons/shield_yellow_loss_control.svg';
import CheckmarkIcon from '../../../icons/Checkmark.svg';
import CheckmarkDarkIcon from '../../../icons/checkmark_dark.svg';
import { selectLossControl } from '../../../selectors/application';
import { selectLossControlFees } from '../../../selectors/quote';
import { getPaymentInterval } from '../../../selectors/routing';
import { setLossControl } from '../../../actions/application';
import { formatCurrency } from '../../../helpers/formatNumber';

interface PropTypes {
  setLossControl: (lossControl: string) => void;
}

function AddOnsServices({ setLossControl }: PropTypes) {
  const paymentInterval = useSelector(getPaymentInterval);
  const isYearly = paymentInterval === 'None';
  const lossControl = useSelector(selectLossControl);
  const lossControlFees = useSelector(selectLossControlFees);
  const hasLossControl = Boolean(lossControl === 'OptIn');

  const handleOnChange = () => {
    setLossControl(hasLossControl ? 'OptOut' : 'OptIn');
  };

  const savingsMessage = hasLossControl
    ? 'You are saving up to 10% on your premium'
    : 'Save up to 10% in premium when you enroll';

  const priceMessage = `Added for ${
    isYearly ? formatCurrency(lossControlFees * 12) : `${formatCurrency(lossControlFees)}/mo`
  }`;

  return (
    <SectionContainer>
      <SectionSubHeader>Manage My Risk</SectionSubHeader>
      <SectionCont isSelected={hasLossControl}>
        <SavingsHeader>
          <PolicyHeaderCont>
            <PolicyTitleCont>
              <Checkbox
                onChange={handleOnChange}
                selected={hasLossControl}
                ariaLabel={`${hasLossControl ? 'deselect' : 'select'} Manage My Risk program`}
                id="manage-my-risk-checkbox"
              />
              <PolicyTitle>Manage My Risk Program</PolicyTitle>
            </PolicyTitleCont>
            <SavingsCont hasLossControl={hasLossControl}>
              {hasLossControl && <CheckmarkSmall src={CheckmarkDarkIcon} alt="checkmark" height={14} width={16} />}
              <SavingsMessage>{savingsMessage}</SavingsMessage>
            </SavingsCont>
          </PolicyHeaderCont>
        </SavingsHeader>
        <div>
          <Description>
            Boost your client's protection with our self-guided and self-directed Manage My Risk Program.{' '}
            <ReadMoreLink
              href="https://explore.coterieinsurance.com/manage-my-risk-policyholders"
              target="_blank"
              rel="noopener"
            >
              Read more
            </ReadMoreLink>
          </Description>
          {hasLossControl && Boolean(lossControlFees) && (
            <PriceCont>
              <Checkmark src={CheckmarkIcon} alt="checkmark" />
              <PolicyPrice>{priceMessage}</PolicyPrice>
            </PriceCont>
          )}
        </div>
      </SectionCont>
    </SectionContainer>
  );
}

const mapDispatchToProps = {
  setLossControl,
};

const PolicyHeaderCont = styled(HeaderSubCont)`
  justify-content: space-between;

  @media (max-width: 767px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 10px;
  }
`;

const SavingsHeader = styled(SectionHeaderCont)`
  display: block;
`;

const PolicyTitleCont = styled.div`
  display: flex;
  flex-direction: row;
  gap: 16px;
  align-items: center;
`;

const SavingsCont = styled.div<{ hasLossControl: boolean }>`
  display: flex;
  padding: 4px 8px;
  align-items: flex-start;
  gap: 8px;
  background: ${(props) => (props.hasLossControl ? '#ecfff8' : props.theme.colors.primary.ten)};
`;

const SavingsMessage = styled.p`
  margin: 0;
  font-size: 12px;
  font-weight: 600;
  line-height: 16px;
  letter-spacing: 0.09px;
`;

const PriceCont = styled.div`
  display: flex;
`;

const CheckmarkSmall = styled.img`
  width: 16px !important;
  height: 14px !important;
`;

const Checkmark = styled.img`
  margin-bottom: 6px;
`;

const ReadMoreLink = styled.a`
  font-size: 16px;
  margin: 24px 0 0px;
  font-family: ${({ theme }) => theme.font.typeface.primary};
  color: ${({ theme }) => theme.pageComponents.shared.infoSideBar.price.textColor} !important;
  font-weight: 600;
  letter-spacing: 0;
  line-height: 19px;
  :hover {
    color: ${({ theme }) => theme.buttons.textOnly.textColor} !important;
  }
`;

const Description = styled.p`
  font-family: ${({ theme }) => theme.font.typeface.secondary};
`;

export default connect(null, mapDispatchToProps)(AddOnsServices);
